import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import PageLayout from "../components/shared/PageLayout";

const FAQ = () => {
  // FAQ Schema for rich results
  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Where do fortune cookies come from?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Despite being associated with Chinese cuisine in America, fortune cookies actually originated in Japan. They were brought to the United States by Japanese immigrants in the early 1900s and later became popular in Chinese restaurants after World War II."
        }
      },
      {
        "@type": "Question",
        "name": "Who writes fortune cookie messages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fortune cookie messages are typically written by professional writers hired by fortune cookie companies. These writers create a mix of inspirational quotes, vague predictions, proverbs, and lucky numbers. Some companies also accept submissions from the public."
        }
      },
      {
        "@type": "Question",
        "name": "What do the numbers in fortune cookies mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The numbers in fortune cookies are commonly referred to as 'lucky numbers.' They have no inherent meaning but are often used by recipients for lottery selections or as lucky numbers for games of chance. Typically, six numbers are provided to match lottery ticket formats."
        }
      },
      {
        "@type": "Question",
        "name": "Are fortune cookies really Chinese?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, fortune cookies are not Chinese in origin. They originated in Japan and were adapted and popularized in the United States. They are rarely found in China and are considered an American invention."
        }
      },
      {
        "@type": "Question",
        "name": "How are fortune cookies made?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fortune cookies are made from a simple batter of flour, sugar, vanilla, and sesame seed oil. The batter is baked into thin wafers, and while still hot and flexible, a fortune is placed in the center. The cookie is then folded into its characteristic shape and allowed to cool and harden."
        }
      },
      {
        "@type": "Question",
        "name": "Do fortune cookies predict the future?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, fortune cookies do not predict the future. The messages inside are written well in advance by professional writers and are designed to be general enough to apply to many situations. They're meant for entertainment rather than genuine fortune-telling."
        }
      },
      {
        "@type": "Question",
        "name": "When should you eat the fortune cookie?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Traditionally, fortune cookies are served at the end of a meal as a dessert. There's a superstition that you should read your fortune before eating the cookie for good luck, but there's no official rule."
        }
      },
      {
        "@type": "Question",
        "name": "Why are fortune cookies so popular?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fortune cookies are popular because they add an element of fun, surprise, and personal engagement to the dining experience. The anticipation of reading your fortune creates a memorable moment that has become a cultural tradition in American Chinese restaurants."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Fortune Cookie FAQs | Daily Fortune</title>
        <meta 
          name="description" 
          content="Get answers to common questions about fortune cookies - their origin, meaning, how they're made, and more. Discover the secrets behind these prophetic treats." 
        />
        <meta 
          name="keywords" 
          content="fortune cookie FAQ, what do fortune cookies mean, who writes fortune cookie fortunes, fortune cookie history, fortune cookie numbers meaning" 
        />
        <meta property="og:title" content="Fortune Cookie FAQs | Daily Fortune" />
        <meta 
          property="og:description" 
          content="Discover the answers to the most common questions about fortune cookies and their mystical messages." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/faq" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fortune Cookie FAQs | Daily Fortune" />
        <meta 
          name="twitter:description" 
          content="Learn everything you've ever wanted to know about fortune cookies - from their origins to how they're made." 
        />
        <link rel="canonical" href="/faq" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchemaData)}
        </script>
      </Helmet>

      <PageLayout>
        <motion.div 
          className="space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <motion.h1 
              className="text-4xl font-light mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Fortune Cookie FAQs
            </motion.h1>
            <motion.p
              className="text-muted-foreground text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Everything you've ever wanted to know about these prophetic treats
            </motion.p>
          </div>

          <motion.div
            className="bg-card rounded-xl p-6 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-lg font-medium">Where do fortune cookies come from?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Despite being associated with Chinese cuisine in America, fortune cookies actually originated in Japan. They were brought to the United States by Japanese immigrants in the early 1900s and later became popular in Chinese restaurants after World War II.</p>
                  <p className="mt-2">The modern fortune cookie as we know it today was largely popularized in California, where they were mass-produced and distributed to Chinese restaurants across the United States.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-lg font-medium">Who writes fortune cookie messages?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Fortune cookie messages are typically written by professional writers hired by fortune cookie companies. These writers create a mix of inspirational quotes, vague predictions, proverbs, and lucky numbers. Some companies also accept submissions from the public.</p>
                  <p className="mt-2">The messages are designed to be universally applicable, positive, and sometimes humorous. Writers aim to create fortunes that could reasonably apply to anyone's life situation.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="text-lg font-medium">What do the numbers in fortune cookies mean?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>The numbers in fortune cookies are commonly referred to as "lucky numbers." They have no inherent meaning but are often used by recipients for lottery selections or as lucky numbers for games of chance. Typically, six numbers are provided to match lottery ticket formats.</p>
                  <p className="mt-2">Some people take these numbers seriously and use them for gambling, while others view them as just another fun element of the fortune cookie experience.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-lg font-medium">Are fortune cookies really Chinese?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>No, fortune cookies are not Chinese in origin. They originated in Japan and were adapted and popularized in the United States. They are rarely found in China and are considered an American invention.</p>
                  <p className="mt-2">When fortune cookies were introduced to China in the 1990s, they were marketed as "genuine American fortune cookies."</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5">
                <AccordionTrigger className="text-lg font-medium">How are fortune cookies made?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Fortune cookies are made from a simple batter of flour, sugar, vanilla, and sesame seed oil. The batter is baked into thin wafers, and while still hot and flexible, a fortune is placed in the center. The cookie is then folded into its characteristic shape and allowed to cool and harden.</p>
                  <p className="mt-2">While early fortune cookies were made by hand, modern production is largely automated, with specialized machines that can produce thousands of cookies per hour.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6">
                <AccordionTrigger className="text-lg font-medium">Do fortune cookies predict the future?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>No, fortune cookies do not predict the future. The messages inside are written well in advance by professional writers and are designed to be general enough to apply to many situations. They're meant for entertainment rather than genuine fortune-telling.</p>
                  <p className="mt-2">That said, many people enjoy the coincidences when a fortune seems particularly relevant to their life, and some keep fortunes that resonated with them as mementos or good luck charms.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7">
                <AccordionTrigger className="text-lg font-medium">When should you eat the fortune cookie?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Traditionally, fortune cookies are served at the end of a meal as a dessert. There's a superstition that you should read your fortune before eating the cookie for good luck, but there's no official rule.</p>
                  <p className="mt-2">Some other common superstitions include: the fortune won't come true if you don't eat the cookie, you should never read someone else's fortune aloud, and your fortune will be more accurate if someone else gives you the cookie rather than choosing it yourself.</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-8">
                <AccordionTrigger className="text-lg font-medium">Why are fortune cookies so popular?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Fortune cookies are popular because they add an element of fun, surprise, and personal engagement to the dining experience. The anticipation of reading your fortune creates a memorable moment that has become a cultural tradition in American Chinese restaurants.</p>
                  <p className="mt-2">Their popularity has also been bolstered by their appearance in popular media, their use as marketing tools, and their status as a uniquely American-Chinese cultural icon.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>

          <div className="text-center mt-12 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
            >
              <Link to="/">
                <Button className="bg-cookie-dark hover:bg-cookie-light text-white">
                  Get Your Daily Fortune
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </PageLayout>
    </>
  );
};

export default FAQ;
